import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, UserRole } from '@/types'
import { api } from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isInitialized = ref(false)
  const currentOrganizationSlug = ref<string | null>(null)
  const currentOrganizationName = ref<string | null>(null)
  const currentOrganizationLogoUrl = ref<string | null>(null)
  const currentOrganizationRole = ref<UserRole | null>(null)

  const isAuthenticated = computed(() => !!user.value)

  async function initialize() {
    try {
      const response = await api.get('/user')
      user.value = response.data.data
    } catch {
      user.value = null
      isInitialized.value = true
      return
    }

    try {
      if (!currentOrganizationSlug.value) {
        const orgsResponse = await api.get('/orgs')
        const orgs = orgsResponse.data.data
        if (orgs && orgs.length > 0) {
          currentOrganizationSlug.value = orgs[0].slug
          currentOrganizationName.value = orgs[0].name
          currentOrganizationLogoUrl.value = orgs[0].logo_url ?? null
          currentOrganizationRole.value = orgs[0].pivot?.role ?? orgs[0].role ?? null
        }
      }
    } catch {
      // Orgs fetch failed — user stays authenticated, slug stays empty
    } finally {
      isInitialized.value = true
    }
  }

  async function login(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password })
    const data = response.data.data
    if (data.token) {
      localStorage.setItem('auth_token', data.token)
    }
    user.value = data.user
    return response.data
  }

  async function register(name: string, email: string, password: string, passwordConfirmation: string) {
    const response = await api.post('/auth/register', {
      name,
      email,
      password,
      password_confirmation: passwordConfirmation,
    })
    const data = response.data.data
    if (data.token) {
      localStorage.setItem('auth_token', data.token)
    }
    user.value = data.user
    return response.data
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      localStorage.removeItem('auth_token')
      user.value = null
      currentOrganizationSlug.value = null
      currentOrganizationRole.value = null
    }
  }

  function setCurrentOrganization(slug: string, name?: string, logoUrl?: string | null, role?: UserRole | null) {
    currentOrganizationSlug.value = slug
    if (name) currentOrganizationName.value = name
    if (logoUrl !== undefined) currentOrganizationLogoUrl.value = logoUrl
    if (role !== undefined) currentOrganizationRole.value = role ?? null
  }

  return {
    user,
    isInitialized,
    isAuthenticated,
    currentOrganizationSlug,
    currentOrganizationName,
    currentOrganizationLogoUrl,
    currentOrganizationRole,
    initialize,
    login,
    register,
    logout,
    setCurrentOrganization,
  }
})
